import { IconButton } from "@mui/material";
import { ArrowBackOutlined as ArrowBackIcon } from "@mui/icons-material";

import { MODAL_STEPS } from "../../../../constants/modal";
import { getCloseButtonSize, useIsMobile } from "../../../../utils";
import { useMediaQuery } from "react-responsive";
import {
  subtitlesStyles,
  titleStyles,
  uxGender,
  uxRouteNone,
  uxRouteQuestionOnly,
  uxRouteQuestionPushScan,
} from "../../../../configs";
import { findBrandByDomain } from "../../../../configs/configLoader";
import { useTranslation } from "react-i18next";
import BackIconGivency from "../../../../assets/icons/BackIconGivency";

import "./index.css";

interface BackArrowButtonProps {
  step: any | null;
  brandName?: string;
  handleGoBack: (currentStep: any | null) => void;
}

export const BackArrowButton = ({
  step,
  brandName,
  handleGoBack,
}: BackArrowButtonProps) => {
  const isMobile = useIsMobile();

  const isLargeHeightRelative = useMediaQuery({
    maxHeight: 830,
    minWidth: 767,
  });

  const isMediumHeightRelative = useMediaQuery({
    maxHeight: 678,
    minWidth: 767,
  });

  const { t } = useTranslation("components/intro");

  const urlParameters = new URLSearchParams(window.location.search);
  const isLingerie = urlParameters.get("lingerie") === "true";
  const isFootwear =
    urlParameters.get("category") === "footwear" ||
    urlParameters.get("footwear") === "true";

  const brandDefined = findBrandByDomain();

  const isSRP = brandDefined?.name === "SRP";
  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isSportyAndRich = brandDefined?.name === "Sporty & Rich";
  const isVelour = brandDefined?.name === "Velour Garments";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isLingerieFirstStep = () =>
    isLingerie && step?.number === MODAL_STEPS.BREASTS.number;

  const isMobileVelourIntro = () =>
    isMobile && isVelour && step?.number === MODAL_STEPS.INTRO_MOBILE.number;

  const isFootwearGenderPreselected = () =>
    (uxRouteQuestionOnly || uxRouteQuestionPushScan) &&
    isFootwear &&
    step?.number === MODAL_STEPS.SHOE_GENDER.number;

  const getPreselectedGenderIntro = () =>
    uxGender ? MODAL_STEPS.INTRO_MOBILE.number : null;

  const isFootwearNoneStep = () => {
    const footwearFirtStep = uxRouteNone
      ? MODAL_STEPS.BREASTS?.number
      : MODAL_STEPS.DEVICE_SELECT?.number;

    return isFootwear && step?.number === footwearFirtStep;
  };

  const hiddenSteps = [
    MODAL_STEPS.DEVICE_SELECT.number,
    MODAL_STEPS.CONSENT.number,
    MODAL_STEPS.GENDER.number,
    MODAL_STEPS.INTRO.number,
    getPreselectedGenderIntro(),
    MODAL_STEPS.ERROR.number,
    MODAL_STEPS.RESULT.number,
    MODAL_STEPS.UNAVAILABLE_RESULT.number,
    MODAL_STEPS.ANTI_BRACKETING.number,
  ].filter((item: number | null) => item !== null);

  const shouldHide =
    !step ||
    step.number < 0 ||
    isLingerieFirstStep() ||
    isMobileVelourIntro() ||
    isFootwearGenderPreselected() ||
    isFootwearNoneStep() ||
    hiddenSteps.includes(step.number);

  const isGivenchyRequired =
    isGivenchy &&
    (step?.number === MODAL_STEPS.ERROR.number ||
      step?.number === MODAL_STEPS.UNAVAILABLE_RESULT.number ||
      step?.number === MODAL_STEPS.RESULT.number);

  const shouldShow = isGivenchyRequired || isTDF || !shouldHide;

  const getTopValue = (): string => {
    if (isSRP) return "12px";
    if (isBlackstore || isIntersport) return "5px";
    if (isFootwear && !isSportyAndRich) return "10px";
    return "10px";
  };

  const getWrapperTop = (): string => {
    if (isLargeHeightRelative && !isSportyAndRich) return "5px";
    if (isMediumHeightRelative && isSportyAndRich) return "5px";
    return "inherit";
  };

  if (!shouldShow)
    return (
      <div
        className="left-arrow"
        style={{ position: "relative", left: "20px", top: getWrapperTop() }}
      />
    );

  const arrowButtonSize = getCloseButtonSize(
    isMobile,
    brandName,
    { mobile: "16px", desktop: "18px" },
    { mobile: "27px", desktop: "30px" },
  );

  if (isGivenchy && !isMobile) {
    const textStyles = {
      ...subtitlesStyles,
      color: titleStyles.color ?? "black",
      fontSize: `calc(${subtitlesStyles.fontSize} - 1px)`,
      cursor: "pointer",
      position: isMobile ? "fixed" : "",
      left: "20px",
      top: getTopValue(),
      textTransform: "uppercase",
    };
    return (
      <span
        role="button"
        tabIndex={0}
        style={textStyles}
        onClick={() => handleGoBack(step)}
        // onKeyDown={(e) => {
        //   if (e.key === "Space") handleGoBack(step);
        // }}
      >
        {t("Back")}
      </span>
    );
  }

  if (isTDF) {
    return (
      <span
        style={{
          ...titleStyles,
          textTransform: "uppercase",
          letterSpacing: "4%",
          position: "relative",
          bottom: "10px",
        }}
      >
        {t("steps.intro")}
      </span>
    );
  }

  return (
    <div
      className="left-arrow"
      style={{
        position: "absolute",
        left: isGivenchy && isMobile ? "5px" : "20px",
        top: getWrapperTop(),
      }}
    >
      <IconButton
        aria-label={t("Back")}
        sx={{
          zIndex: 1,
          padding: "3px",
          borderRadius: "5px",
          position: "fixed",
          top: getTopValue(),
        }}
        onClick={() => {
          handleGoBack(step);
        }}
      >
        {isGivenchy && isMobile ? (
          <BackIconGivency
            className="icon"
            style={{ width: "40px", height: "40px" }}
          />
        ) : (
          <ArrowBackIcon
            style={{
              color: "black",
              width: arrowButtonSize,
              height: arrowButtonSize,
            }}
          />
        )}
      </IconButton>
    </div>
  );
};

export default BackArrowButton;
